import { atom } from 'jotai';
import { atomWithStorage, createJSONStorage } from 'jotai/utils';

import { EnvConfig } from '@config';
import { GlobalStateKeyEnum } from '@lib/enums';
import { getEnumValues } from '@lib/utils/getEnumValues';

export enum FeatureFlagEnum {
  CmsBasedVerifyDetailsContent,
  ConsumerActivityLog,
  ConsumerAnchorSearch,
  ContractActions,
  ContractActivityLog,
  ContractPdf,
  PayByBank,
  PayByBankAutoRefresh,
  TabSystem,
  Saves,
  TransactionActions,
  CsRequestRefund,
  SendLetterOfCancellation,
  SendPrrBreakdown,
  DeferredCallRedirect,
  Training,
  PostDateOnMissingDownPayment,
  CustomerOwnsVehicle,
  StopTransfer,
  ConsumerPageRedesign,
  NotesV2,
  DarkModeToggle,
  DownloadPrrBreakdown,
  EditCancellationDetails,
  CarfaxAllReports,
  EnduranceClaimsHistory,
  FavoriteConsumersDevOnly,
  HomeWarrantyClaims,
}

export const FeatureFlagDisplayValue = {
  [FeatureFlagEnum.CmsBasedVerifyDetailsContent]: 'CMS Based Verify Details Content',
  [FeatureFlagEnum.ConsumerActivityLog]: 'Consumer Activity Log',
  [FeatureFlagEnum.ConsumerAnchorSearch]: 'Consumer Anchor Search',
  [FeatureFlagEnum.ContractActions]: 'Contract Actions',
  [FeatureFlagEnum.ContractActivityLog]: 'Contract Activity Log',
  [FeatureFlagEnum.ContractPdf]: 'Contract PDF link',
  [FeatureFlagEnum.PayByBank]: 'Pay by Bank',
  [FeatureFlagEnum.PayByBankAutoRefresh]: 'Pay by Bank Auto Refresh',
  [FeatureFlagEnum.TabSystem]: 'Tab System',
  [FeatureFlagEnum.Saves]: 'Saves',
  [FeatureFlagEnum.TransactionActions]: 'Transaction Actions',
  [FeatureFlagEnum.CsRequestRefund]: 'CS Request Refund',
  [FeatureFlagEnum.SendLetterOfCancellation]: 'Send LoC Email',
  [FeatureFlagEnum.SendPrrBreakdown]: 'Send PRR Email',
  [FeatureFlagEnum.DeferredCallRedirect]: 'Deferred Call Redirect',
  [FeatureFlagEnum.Training]: 'Training',
  [FeatureFlagEnum.PostDateOnMissingDownPayment]: 'Post Date On Missing Down Payment',
  [FeatureFlagEnum.CustomerOwnsVehicle]: 'Customer Owns Vehicle',
  [FeatureFlagEnum.StopTransfer]: 'Stop Transfer',
  [FeatureFlagEnum.ConsumerPageRedesign]: 'Consumer Page Redesign',
  [FeatureFlagEnum.NotesV2]: 'Notes V2',
  [FeatureFlagEnum.DarkModeToggle]: 'Dark Mode Toggle',
  [FeatureFlagEnum.DownloadPrrBreakdown]: 'Download PRR Breakdown',
  [FeatureFlagEnum.EditCancellationDetails]: 'Edit Cancellation Details',
  [FeatureFlagEnum.CarfaxAllReports]: 'Carfax All Reports',
  [FeatureFlagEnum.EnduranceClaimsHistory]: 'Endurance Claims History',
  [FeatureFlagEnum.FavoriteConsumersDevOnly]: 'Dashboard Favorites [DEV-ONLY]',
  [FeatureFlagEnum.HomeWarrantyClaims]: 'Home Warranty Claims',
} satisfies Record<FeatureFlagEnum, string>;

export type TFeatureFlagState = Readonly<{
  featureFlags: FeatureFlagEnum[];
}>;

type TFeatureFlagOverrides = Partial<Record<keyof typeof FeatureFlagEnum, boolean>>;

const INITIAL_FEATURE_STATE = {
  featureFlags: [
    FeatureFlagEnum.CmsBasedVerifyDetailsContent,
    FeatureFlagEnum.ConsumerAnchorSearch,
    FeatureFlagEnum.ContractActivityLog,
    FeatureFlagEnum.ContractActions,
    FeatureFlagEnum.ContractPdf,
    FeatureFlagEnum.CsRequestRefund,
    FeatureFlagEnum.Saves,
    FeatureFlagEnum.TransactionActions,
    FeatureFlagEnum.SendLetterOfCancellation,
    FeatureFlagEnum.SendPrrBreakdown,
    FeatureFlagEnum.DeferredCallRedirect,
    FeatureFlagEnum.CustomerOwnsVehicle,
    FeatureFlagEnum.PostDateOnMissingDownPayment,
    FeatureFlagEnum.StopTransfer,
    FeatureFlagEnum.DownloadPrrBreakdown,
    FeatureFlagEnum.DarkModeToggle,
    FeatureFlagEnum.NotesV2,
    EnvConfig.appEnv === 'qa' ? FeatureFlagEnum.Training : undefined,
    EnvConfig.appEnv === 'local' || EnvConfig.appEnv === 'development'
      ? FeatureFlagEnum.FavoriteConsumersDevOnly
      : undefined,
  ].filter((flag) => flag != null),
} satisfies TFeatureFlagState;

const DEFAULT_ENABLED = new Set(INITIAL_FEATURE_STATE.featureFlags);

export function computeEffectiveFlags(overrides: TFeatureFlagOverrides): TFeatureFlagState {
  const featureFlags = getEnumValues(FeatureFlagEnum).filter((flag) => {
    const override = overrides[FeatureFlagEnum[flag] as keyof typeof FeatureFlagEnum];
    return typeof override === 'boolean' ? override : DEFAULT_ENABLED.has(flag);
  });

  return { featureFlags };
}

export function toggleFlagOverride(
  overrides: TFeatureFlagOverrides,
  flag: FeatureFlagEnum,
): TFeatureFlagOverrides {
  const name = FeatureFlagEnum[flag] as keyof typeof FeatureFlagEnum;
  const isDefault = DEFAULT_ENABLED.has(flag);
  const current = typeof overrides[name] === 'boolean' ? overrides[name] : isDefault;
  const next = { ...overrides };

  if (current === isDefault) {
    next[name] = !current;
  } else {
    delete next[name];
  }

  return next;
}

// Persist only the user's overrides to sessionStorage so DevTools toggles survive refreshes but reset when the tab closes.
const sessionStorageProvider = createJSONStorage<TFeatureFlagOverrides>(
  () => (typeof window !== 'undefined' ? window.sessionStorage : undefined) as Storage,
);

const featureFlagOverridesAtom = atomWithStorage<TFeatureFlagOverrides>(
  GlobalStateKeyEnum.FeatureFlag,
  {},
  sessionStorageProvider,
  { getOnInit: true },
);

const featureFlagAtom = atom<TFeatureFlagState, [FeatureFlagEnum], void>(
  (get) => computeEffectiveFlags(get(featureFlagOverridesAtom)),
  (get, set, flag) =>
    set(featureFlagOverridesAtom, toggleFlagOverride(get(featureFlagOverridesAtom), flag)),
);

export { featureFlagAtom };
